/**
 * Hardcoded "Tạo mặc định" lists for ATS reference tables.
 *
 * Single source of truth shared between:
 *  - per-resource list pages (Source / EmployeeType / WorkArrangement / RecruitmentType /
 *    RejectionReason / Pipeline)
 *  - the onboarding "Tạo dữ liệu mẫu" action (calls the same bulk endpoints
 *    behind the scenes)
 *
 * Each locale list defines the names that will be sent to the corresponding
 * `POST /<resource>/bulk` endpoint; duplicates are skipped silently on the
 * server so calling these is idempotent.
 */

export type Locale = 'vi' | 'en';

const DEFAULT_LOCALE: Locale = 'vi';

function resolveLocale(language: string | undefined): Locale {
  if (!language) return DEFAULT_LOCALE;
  const lower = language.toLowerCase();
  if (lower.startsWith('en')) return 'en';
  if (lower.startsWith('vi')) return 'vi';
  return DEFAULT_LOCALE;
}

// --- Source -----------------------------------------------------------------

export const DEFAULT_SOURCES_BY_LOCALE: Record<Locale, readonly string[]> = {
  vi: ['Khác', 'Facebook', 'LinkedIn', 'Giới thiệu nội bộ', 'Website'],
  en: ['Other', 'Facebook', 'LinkedIn', 'Internal referral', 'Website'],
};

export function getDefaultSources(language: string | undefined): readonly string[] {
  return DEFAULT_SOURCES_BY_LOCALE[resolveLocale(language)];
}

// --- EmployeeType -----------------------------------------------------------

export const DEFAULT_EMPLOYEE_TYPES_BY_LOCALE: Record<Locale, readonly string[]> = {
  vi: ['Toàn thời gian', 'Bán thời gian', 'Thời vụ', 'Thực tập sinh'],
  en: ['Full-time', 'Part-time', 'Seasonal', 'Intern'],
};

export function getDefaultEmployeeTypes(language: string | undefined): readonly string[] {
  return DEFAULT_EMPLOYEE_TYPES_BY_LOCALE[resolveLocale(language)];
}

// --- WorkArrangement --------------------------------------------------------

export const DEFAULT_WORK_ARRANGEMENTS_BY_LOCALE: Record<Locale, readonly string[]> = {
  vi: ['Tại văn phòng', 'Từ xa', 'Kết hợp'],
  en: ['Onsite', 'Remote', 'Hybrid'],
};

export function getDefaultWorkArrangements(language: string | undefined): readonly string[] {
  return DEFAULT_WORK_ARRANGEMENTS_BY_LOCALE[resolveLocale(language)];
}

// --- RecruitmentType --------------------------------------------------------

export const DEFAULT_RECRUITMENT_TYPES_BY_LOCALE: Record<Locale, readonly string[]> = {
  vi: ['Tuyển mới', 'Tuyển thay thế'],
  en: ['New hire', 'Replacement'],
};

export function getDefaultRecruitmentTypes(language: string | undefined): readonly string[] {
  return DEFAULT_RECRUITMENT_TYPES_BY_LOCALE[resolveLocale(language)];
}

// --- RejectionReason --------------------------------------------------------

export const DEFAULT_REJECTION_REASONS_BY_LOCALE: Record<Locale, readonly string[]> = {
  vi: [
    'Khác',
    'Mức lương ứng viên đưa ra không phù hợp',
    'Ứng viên có định hướng nghề nghiệp khác',
    'Thiếu kinh nghiệm làm việc',
    'Năng lực chuyên môn chưa phù hợp',
    'Không tham gia làm bài test/phỏng vấn',
    'Không đáp ứng được yêu cầu công việc',
    'Không phù hợp với văn hóa công ty',
    'Đã nhận offer khác',
    'Thời gian làm việc không phù hợp',
    'Nhà xa',
    'Lương không đáp ứng',
    'Đã đóng đợt tuyển dụng',
    'Trái ngành',
  ],
  en: [
    'Other',
    'Salary expectation does not match',
    'Different career direction',
    'Lacks work experience',
    'Insufficient technical skills',
    'Did not take the test/interview',
    'Does not meet job requirements',
    'Not a culture fit',
    'Accepted another offer',
    'Working hours not suitable',
    'Lives too far away',
    'Salary offer not accepted',
    'Recruitment closed',
    'Wrong industry',
  ],
};

export function getDefaultRejectionReasons(language: string | undefined): readonly string[] {
  return DEFAULT_REJECTION_REASONS_BY_LOCALE[resolveLocale(language)];
}

// --- CalendarEventType ------------------------------------------------------

export const DEFAULT_EVENT_TYPES_BY_LOCALE: Record<Locale, readonly string[]> = {
  vi: ['Phỏng vấn', 'Sàng lọc qua điện thoại', 'Bài kiểm tra', 'Offer', 'Onboard', 'Khác'],
  en: ['Interview', 'Phone Screen', 'Test', 'Offer', 'Onboard', 'Other'],
};

export function getDefaultEventTypes(language: string | undefined): readonly string[] {
  return DEFAULT_EVENT_TYPES_BY_LOCALE[resolveLocale(language)];
}

// --- Pipeline ---------------------------------------------------------------

export interface DefaultPipelineRound {
  name: string;
  round_type: string;
}

export interface DefaultPipeline {
  name: string;
  rounds: DefaultPipelineRound[];
}

export const DEFAULT_PIPELINES_BY_LOCALE: Record<Locale, readonly DefaultPipeline[]> = {
  vi: [
    {
      name: 'Pipeline mặc định',
      rounds: [
        { name: 'Nhận hồ sơ', round_type: 'application_received' },
        { name: 'Sàng lọc', round_type: 'screening' },
        { name: 'Phỏng vấn', round_type: 'interview' },
        { name: 'Gửi Offer', round_type: 'offer_sent' },
        { name: 'Đã tuyển', round_type: 'hired' },
      ],
    },
  ],
  en: [
    {
      name: 'Default pipeline',
      rounds: [
        { name: 'Application received', round_type: 'application_received' },
        { name: 'Screening', round_type: 'screening' },
        { name: 'Interview', round_type: 'interview' },
        { name: 'Offer sent', round_type: 'offer_sent' },
        { name: 'Hired', round_type: 'hired' },
      ],
    },
  ],
};

export function getDefaultPipelines(
  language: string | undefined,
): readonly DefaultPipeline[] {
  return DEFAULT_PIPELINES_BY_LOCALE[resolveLocale(language)];
}
